import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toDraft = (value) => (value === null ? "" : String(value));

const NumberStepper = ({ label, value, min, max, step, seed, onChange }) => {
  const theme = useTheme();
  const [draft, setDraft] = useState(toDraft(value));
  const isEmpty = value === null;

  useEffect(() => {
    setDraft(toDraft(value));
  }, [value]);

  const commitDraft = () => {
    if (draft.trim() === "") {
      if (!isEmpty) {
        onChange(null);
      }

      return;
    }

    const parsed = Number(draft);

    if (Number.isNaN(parsed) || !Number.isInteger(parsed)) {
      setDraft(toDraft(value));
      return;
    }

    const next = clamp(parsed, min, max);

    if (next === value) {
      setDraft(toDraft(next));
      return;
    }

    onChange(next);
  };

  const stepBy = (direction) => {
    onChange(isEmpty ? seed : clamp(value + direction * step, min, max));
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 0.5, color: theme.palette.lightText.main }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${theme.palette.border.main}`,
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <IconButton
          size="small"
          aria-label={`Decrease ${label}`}
          onClick={() => stepBy(-1)}
          disabled={!isEmpty && value <= min}
          sx={{ borderRadius: 0 }}
        >
          <Remove fontSize="small" />
        </IconButton>
        <InputBase
          value={draft}
          placeholder="Any"
          inputProps={{ inputMode: "numeric", "aria-label": label }}
          onChange={(event) => {
            const nextDraft = event.target.value;

            if (nextDraft !== "" && !/^\d+$/.test(nextDraft)) {
              return;
            }

            setDraft(nextDraft);
          }}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.target.blur();
            }
          }}
          sx={{
            flex: 1,
            fontSize: "0.9375rem",
            fontWeight: 600,
            "& input": { textAlign: "center", p: 0 },
          }}
        />
        <IconButton
          size="small"
          aria-label={`Increase ${label}`}
          onClick={() => stepBy(1)}
          disabled={!isEmpty && value >= max}
          sx={{ borderRadius: 0 }}
        >
          <Add fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default NumberStepper;
